import { ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'

import { useAiChat } from '@factorialco/f0-react'
import {
  ApplicationFrame,
  Menu,
  MenuCategory,
  MenuProps,
  SearchBar,
  Sidebar,
  SidebarFooter,
  SidebarHeader,
} from '@factorialco/f0-react/dist/experimental'
import { Exit, Image, Sliders, Sparkles, StarFilled } from '@factorialco/f0-react/icons/app'
import { EnvironmentContext } from 'config/environment'
import {
  FactorialOneLayoutQuery,
  FetchSidebarFavoritesDocument,
  FetchSidebarFavoritesQuery,
  OneWaitlistDocument,
  ReferralProgramPoliciesDocument,
  RemoveResourceFromFavoritesDocument,
  ReorderFavouriteResourcesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { ActivitiesRenderer, useActivities } from 'modules/core/lib/activities'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import {
  setCollapsedCategory,
  setSortedCategories,
  useSidebarItemByIdentifier,
} from 'modules/core/lib/sidebar'

import { useAiAgentFeature } from 'modules/ai/hooks/useAiAgentFeature'
import { OneProvider } from 'modules/ai/lib/useOne'
import { useCommandBar } from 'modules/command_bar/lib/commandBarContext'
import { useCompanies } from 'modules/factorial_id/hooks'
import { useSwitchCompany } from 'modules/factorial_id/hooks'
import useAiPromotion from 'modules/layout/components/RootLayout/hooks/useAiPromotion'
import { FAVORITE_EVENTS } from 'modules/layout/lib/tracking'
import { useUnreadNotificationCount } from 'modules/notifications/hooks/useNotificationUnreadCount'
import { reward } from 'modules/referral_program/components/helpers'

import { SidebarFavoritesProvider } from './SidebarFavoritesProvider'
import { useWelcomeScreenSuggestions } from './useWelcomeScreenSuggestions'

interface Props {
  children: ReactNode
  data: FactorialOneLayoutQuery
  sidebar: {
    loading: boolean
    menuCategories: MenuCategory[]
    favorites: FetchSidebarFavoritesQuery['layout']['favouritesConnection']['nodes'] | undefined
  }
  banner: ReactNode
}

const AiChatPlaceholderReset: React.FC = () => {
  const { open, setPlaceholders } = useAiChat()

  useEffect(() => {
    if (!open) {
      setPlaceholders([])
    }
  }, [open, setPlaceholders])

  return null
}

const isPersonAvatarType = (type: string | undefined | null): type is 'person' => {
  return type === 'person'
}

const mapFavoriteRecordsToMenuItems = (
  items: Props['sidebar']['favorites'] = [],
  sidebarItemByIdentifier: ReturnType<typeof useSidebarItemByIdentifier>
): MenuProps['favorites'] => {
  return items.map((item) => {
    const sidebarItem = sidebarItemByIdentifier[item.icon]
    const tooltip = sidebarItem?.name || item.label

    if (item.avatar && item.avatarType) {
      const avatarType = item.avatarType === 'generic' ? ('company' as const) : item.avatarType
      const isPerson = isPersonAvatarType(avatarType)

      const avatar = isPerson
        ? {
            type: 'person' as const,
            src: item.avatar,
            firstName: item.label,
            lastName: '',
          }
        : {
            type: 'company' as const,
            src: item.avatar,
            name: item.label,
          }

      return {
        label: item.label,
        href: item.url,
        type: 'avatar',
        tooltip,
        onClick: () => {
          track(FAVORITE_EVENTS.navigate, { url: item.url, label: item.label })
        },
        avatar,
      }
    }

    return {
      label: item.label,
      href: item.url,
      type: 'icon',
      tooltip,
      icon: sidebarItem?.factorialOneIcon ?? StarFilled,
      onClick: () => {
        track(FAVORITE_EVENTS.navigate, { url: item.url, label: item.label })
      },
    }
  })
}

export const FactorialOneLayout = ({ children, data, sidebar, banner }: Props) => {
  const { hasUnreadNotifications } = useUnreadNotificationCount()
  const sidebarItemByIdentifier = useSidebarItemByIdentifier()

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const location = useLocation()
  const previousPathnameRef = useRef(location.pathname)
  const { setOpen } = useCommandBar()
  const { toggleActivity } = useActivities()
  const { mastraLocation, ignoreUpsell } = useContext(EnvironmentContext)
  const { employee, company, isAdminBecome, isStaffBecome } = useCurrentSession()
  const { loading: aiLoading, enabled: aiEnabled } = useAiAgentFeature()
  const welcomeScreenSuggestions = useWelcomeScreenSuggestions()
  const menuCategories = sidebar.menuCategories ?? []
  const sidebarFavorites = sidebar.favorites ?? []
  const itInventoryBasePath = useMemo(() => {
    return navigation.it_management.fullPath().replace(/\*+$/, '')
  }, [navigation])
  const { data: oneWaitlistData, loading: oneWaitlistLoading } = useQuery(OneWaitlistDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const aiChatloading = aiLoading || oneWaitlistLoading
  const isAiAgentFeatureEnabled = !!oneWaitlistData?.features.featuresConnection.nodes[0]?.enabled
  const isAiEnabled = (aiEnabled && isAiAgentFeatureEnabled) || (aiEnabled && ignoreUpsell)
  const isBecome = isAdminBecome || isStaffBecome

  const { aiPromotion } = useAiPromotion({
    isAiChatActive: isAiEnabled,
    aiEnabled,
    employeeName: employee?.firstName ?? '',
  })

  useEffect(() => {
    if (
      location.pathname === itInventoryBasePath &&
      !previousPathnameRef.current.includes(itInventoryBasePath)
    ) {
      const fromSearchBox = location.search.includes('command_bar=true')
      track(trackingEvents.itManagement.goToItInventory, {
        coming_from: fromSearchBox ? 'search_global' : 'side_bar',
      })
    }
    previousPathnameRef.current = location.pathname
  }, [location.pathname, location.search, itInventoryBasePath])

  const [removeFromFavorites] = useMutation(RemoveResourceFromFavoritesDocument, {
    extractorFn: (data) => data.layoutMutations.deleteLayoutFavourite,
    update: (cache, { data }) => {
      // manually remove the favorite from graphql cache to keep the interaction snappy
      cache.updateQuery({ query: FetchSidebarFavoritesDocument }, (cachedData) => {
        const sidebarFavorites = cachedData?.layout.favouritesConnection.nodes ?? []
        const removedFavorite = data?.layoutMutations.deleteLayoutFavourite.favourite

        if (removedFavorite) {
          const removedIndex = sidebarFavorites.findIndex((fav) => fav.id === removedFavorite.id)
          if (removedIndex > -1) {
            const newSidebarFavorites = [...sidebarFavorites]
            newSidebarFavorites.splice(removedIndex, 1)

            return {
              ...cachedData,
              layout: {
                favouritesConnection: {
                  nodes: newSidebarFavorites,
                },
              },
            }
          }
        }

        return cachedData
      })
    },
    optimisticResponse: (vars) => ({
      layoutMutations: {
        deleteLayoutFavourite: {
          errors: [],
          favourite: {
            ...vars,
            label: '',
            url: '',
            icon: '',
            description: null,
            avatar: null,
            avatarType: null,
            position: -1,
          },
        },
      },
    }),
  })

  const [reorderFavourites] = useMutation(ReorderFavouriteResourcesDocument, {
    extractorFn: (data) => data.layoutMutations.reorderLayoutFavourite,
    update: (cache, { data }) => {
      // manually update new order to avoid refetch
      cache.updateQuery({ query: FetchSidebarFavoritesDocument }, (cachedData) => {
        const updatedOrder = data?.layoutMutations.reorderLayoutFavourite.favourites

        if (updatedOrder) {
          return {
            ...cachedData,
            layout: {
              favouritesConnection: {
                nodes: updatedOrder,
              },
            },
          }
        }

        return cachedData
      })
    },
    optimisticResponse: (vars) => {
      const favoriteById = sidebarFavorites.reduce(
        (acc: Record<string, (typeof sidebarFavorites)[number]>, cur) => {
          acc[cur.id] = cur
          return acc
        },
        {}
      )
      return {
        layoutMutations: {
          reorderLayoutFavourite: {
            errors: [],
            favourites: vars.ids.map((id) => favoriteById[id]).filter(Boolean),
          },
        },
      }
    },
  })

  const isMac = navigator.userAgent.includes('Macintosh')
  const commandKey = isMac ? 'cmd' : 'ctrl'

  const current = data?.apiCore.currentsConnection.nodes[0]
  const currentCompany = data?.apiCore.companiesConnection.nodes[0]
  const currentAccess = current?.access

  // Store user ID in sessionStorage
  if (currentAccess?.id) {
    sessionStorage.setItem('current_user_id', currentAccess.id.toString())
  }

  const accessAvatar = currentAccess?.avatar ? buildUrl(currentAccess.avatar) : undefined

  const canManageCustomization = !!data?.apiCore._policies.companyManageCustomization.can

  const { companies, loading: companiesLoading } = useCompanies()
  const switchCompany = useSwitchCompany()

  const sidebarLoading = sidebar.loading || companiesLoading
  const hasWorkspaceLogo = !!currentCompany?.workspaceLogo

  const onCollapse = (categoryInfo: MenuCategory) => {
    setCollapsedCategory(categoryInfo.id)
  }

  const onSort = (sortedCategories: MenuCategory[]) => {
    setSortedCategories(sortedCategories)
  }

  const handleActivityButtonClick = () => {
    toggleActivity('core.notificationsActivity')
  }

  const favoriteIdByUrl = sidebarFavorites.reduce((acc: Record<string, string>, cur) => {
    acc[cur.url] = cur.id
    return acc
  }, {})

  const { data: policies } = useQuery(ReferralProgramPoliciesDocument)
  const canSeeReferralPage = policies?.apiCore._policies.seeReferralProgramPage.can
  const mainLegalEntity = currentCompany?.legalEntities.find((e) => e.main)
  const isoCountryCode = mainLegalEntity?.country ?? 'es'
  const isBrazil = isoCountryCode === 'br'
  const footerOptions = useMemo(() => {
    const options = []

    if (canSeeReferralPage) {
      options.push({
        href: navigation.settings.referralProgram.fullPath(),
        label: i18n.t('company_settings.general.factorial_referral_program.settings.title'),
        icon: Sparkles,
        description: isBrazil
          ? i18n.t(
              'company_settings.general.factorial_referral_program.settings.description_brazil',
              { amount: reward(isoCountryCode, '4') }
            )
          : i18n.t('company_settings.general.factorial_referral_program.settings.description'),
        onClick: () => {
          track(trackingEvents.settings.clickDropdownReferFactorial)
        },
      })
    }

    options.push(
      {
        href: navigation.personalSettings.fullPath(),
        label: i18n.t('sidebar.user_settings'),
        icon: Sliders,
      },
      // { type: 'separator' },
      {
        href: navigation.logout.fullPath(),
        label: i18n.t('sidebar.logout'),
        icon: Exit,
        critical: true,
      }
    )

    return options
  }, [canSeeReferralPage, navigation, isoCountryCode])

  const trackDropdownClick = () => {
    track('Open settings dropdown')
  }

  return (
    <ApplicationFrame
      ai={{
        enabled: aiChatloading ? false : isAiEnabled,
        runtimeUrl: `${mastraLocation}/copilotkit`,
        agent: 'one-workflow',
        credentials: 'include',
        greeting: i18n.t('common.ai.greeting', { name: employee?.firstName }),
        resizable: true,
        disclaimer: {
          text: i18n.t('common.ai.disclaimer.text'),
          link: helpCenterUrl('one_ai_permissions'),
          linkText: i18n.t('common.ai.disclaimer.link_text'),
        },
        onThumbsUp: (message, { threadId, feedback }) => {
          const blob = new Blob(
            [
              JSON.stringify({
                threadId,
                score: 1,
                comment: feedback,
              }),
            ],
            { type: 'application/json' }
          )

          navigator.sendBeacon(`${mastraLocation}/user-feedback`, blob)
        },
        onThumbsDown: (message, { threadId, feedback }) => {
          const blob = new Blob(
            [
              JSON.stringify({
                threadId,
                score: 0,
                comment: feedback,
              }),
            ],
            { type: 'application/json' }
          )

          navigator.sendBeacon(`${mastraLocation}/user-feedback`, blob)
        },
        welcomeScreenSuggestions,
      }}
      aiPromotion={aiPromotion}
      sidebar={
        !sidebarLoading && (
          <Sidebar
            header={
              <>
                <SidebarHeader
                  companies={companies}
                  selected={currentCompany?.id.toString()}
                  isExpanded
                  onChange={switchCompany}
                  withNotification={canManageCustomization && !hasWorkspaceLogo}
                  additionalOptions={
                    canManageCustomization && !hasWorkspaceLogo && !isBecome
                      ? [
                          {
                            description: i18n.t('sidebar.workspace_logo.description'),
                            label: i18n.t('sidebar.workspace_logo.title'),
                            value: 'upload',
                            icon: Image,
                            onClick: () => {
                              navigate(navigation.settings.customizations.fullPath())
                            },
                          },
                        ]
                      : []
                  }
                />
                <SearchBar
                  onClick={() => {
                    setOpen(true)
                  }}
                  placeholder={i18n.t('command_bar.search')}
                  shortcut={[commandKey, 'k']}
                />
              </>
            }
            body={
              <Menu
                favorites={mapFavoriteRecordsToMenuItems(sidebarFavorites, sidebarItemByIdentifier)}
                onFavoritesChange={(items) => {
                  const hasRemovedItem = sidebarFavorites.length > items.length
                  if (hasRemovedItem) {
                    const urlsLeft = new Set(items.map((item) => item.href))
                    const removedFavorite = sidebarFavorites.find(
                      (favorite) => !urlsLeft.has(favorite.url)
                    )
                    if (removedFavorite) {
                      track(FAVORITE_EVENTS.remove, {
                        url: removedFavorite.url,
                        label: removedFavorite.label,
                      })
                      removeFromFavorites({ variables: { id: removedFavorite.id } })
                    }
                    return
                  }
                  const newOrderIds = items
                    .map((item) => item.href && favoriteIdByUrl[item.href])
                    .filter((item): item is string => item !== undefined && item !== '')

                  track(FAVORITE_EVENTS.reorder)
                  reorderFavourites({ variables: { ids: newOrderIds } })
                }}
                tree={menuCategories}
                onCollapse={onCollapse}
                onSort={onSort}
              />
            }
            footer={
              <SidebarFooter
                user={{
                  avatarUrl: accessAvatar,
                  firstName: currentAccess?.firstName ?? '',
                  lastName: currentAccess?.lastName ?? '',
                }}
                hasActivityUpdates={hasUnreadNotifications}
                onActivityButtonClick={handleActivityButtonClick}
                options={footerOptions}
                onDropdownClick={trackDropdownClick}
                showActivityButton
              />
            }
          />
        )
      }
      banner={banner}
    >
      <OneProvider enabled={isAiEnabled && !aiChatloading} available={isAiEnabled}>
        <SidebarFavoritesProvider favorites={sidebarFavorites}>{children}</SidebarFavoritesProvider>
        <ActivitiesRenderer />
        <AiChatPlaceholderReset />
      </OneProvider>
    </ApplicationFrame>
  )
}
